import type { Cobertura } from "./tipos";

/**
 * Quanto do que existe o sistema já conhece.
 *
 * <p>Não é enfeite. Um painel que mostra "3 regularizações vencendo" sobre 10
 * de 300 produtos não está informando — está <b>enganando</b>, e engana
 * justamente quem tem menos contexto para desconfiar.
 *
 * <p>A carga é incremental por decisão do PO: os 300+ itens entram à medida que
 * se trabalha, não por importação em bloco. Isso é sensato, e durante meses
 * deixa a cobertura parcial. O que não se aceita é o sistema esconder isso.
 */
export interface Resumo {
  cadastrados: number;
  /** Estimativa declarada por gente. Nulo quando ninguém declarou. */
  declarados: number | null;
  /** Percentual, ou `null` quando não há estimativa contra o que comparar. */
  percentual: number | null;
}

/**
 * @param empresa o **slug** da empresa escolhida, ou `null` para o grupo.
 *        Ele é `string` e não a união de slugs conhecidos porque desde a #24 ele
 *        chega da API — quem afirma quais empresas existem é o servidor, e um
 *        tipo daqui que garantisse a lista seria a lista fixa de volta, disfarçada
 *        de tipo. Slug que não case com nenhuma linha filtra tudo e a base de
 *        cálculo diz isso com todas as letras, que é o comportamento honesto.
 */
export function resumir(cobertura: Cobertura[], empresa: string | null): Resumo {
  const linhas = empresa === null
    ? cobertura
    : cobertura.filter((c) => c.empresa === empresa);

  const cadastrados = linhas.reduce((soma, c) => soma + c.produtosCadastrados, 0);

  // Uma empresa sem estimativa contamina o total: somar as que têm produziria
  // um denominador menor que a realidade, e uma cobertura otimista demais.
  // Sem estimativa completa, não há percentual — e dizer isso é mais honesto
  // que arredondar para cima.
  const todasDeclaradas = linhas.length > 0 && linhas.every((c) => c.produtosDeclarados !== null);
  const declarados = todasDeclaradas
    ? linhas.reduce((soma, c) => soma + (c.produtosDeclarados ?? 0), 0)
    : null;

  return {
    cadastrados,
    declarados,
    percentual:
      declarados !== null && declarados > 0
        ? Math.round((cadastrados / declarados) * 100)
        : null,
  };
}

/** O texto que acompanha todo indicador. Nunca um número sozinho. */
export function baseDeCalculo(resumo: Resumo): string {
  if (resumo.declarados === null) {
    return resumo.cadastrados === 0
      ? "nenhum produto cadastrado ainda"
      : `sobre ${resumo.cadastrados} produtos cadastrados · total no grupo não declarado`;
  }
  return `sobre ${resumo.cadastrados} de ~${resumo.declarados} produtos declarados`;
}
